import { type ShortUrl } from '@air/api';
import {
  type CollectionSubPageInput,
  type CollectionSubPageOutput,
  type ShortUrlResponse,
  type SubPageInput,
  type SubPageOutput,
  type SubPageSettingsOverrideInput,
} from '@air/api/types';

import { getShortUrlUpdateBlock } from '~/swr-hooks/shortUrl/getShortUrlUpdatePresentationContent';

type ShortUrlUpdatePayload = Parameters<typeof ShortUrl.update>[0];

export type ShortUrlMutationPresentationPages =
  | ShortUrlResponse['presentationPages']
  | ShortUrlUpdatePayload['presentationPages'];

type ShortUrlMutationSubPageSettings = NonNullable<(CollectionSubPageInput | CollectionSubPageOutput)['settings']>;

const getShortUrlUpdateSubPageSettings = (settings: ShortUrlMutationSubPageSettings): SubPageSettingsOverrideInput => {
  const { coverImage } = settings;

  if (coverImage === null) {
    // an explicit null clears the sub-page's cover override, so pass it through instead of
    // dropping it on a truthy guard (mirrors the root coverImage in presentationSettings)
    return { coverImage: null };
  }

  if (coverImage?.assetId && coverImage.versionId && coverImage.region) {
    // the read shape's coverImage carries a server-resolved `url`; the write shape must stay a pure
    // asset-version reference so the server re-resolves it on read
    return {
      coverImage: {
        assetId: coverImage.assetId,
        region: coverImage.region,
        versionId: coverImage.versionId,
      },
    };
  }

  return {};
};

const getShortUrlUpdateSubPage = (subPage: SubPageInput | SubPageOutput): SubPageInput => {
  switch (subPage.type) {
    case 'collection':
      // `name`, `parentId`, and `draft` are server-annotated on the read shape only; the
      // strict-whitelisted write path rejects the payload outright if they are echoed back.
      return {
        type: 'collection',
        collectionId: subPage.collectionId,
        content: { blocks: subPage.content.blocks.map(getShortUrlUpdateBlock) },
        ...(subPage.settings ? { settings: getShortUrlUpdateSubPageSettings(subPage.settings) } : {}),
      };
    case 'link':
      // `parentId` is server-annotated on the read shape only — same whitelist rejection.
      return {
        type: 'link',
        label: subPage.label,
        url: subPage.url,
      };
  }
};

/**
 * Converts a `presentationPages` value from the read shape (or an already-write-shaped draft) into
 * the `ShortUrl.update` write shape. The read shape annotates every sub-page with server-owned
 * fields (`name`, `parentId`, `draft`) and resolves `url`s inside sub-page content blocks and cover
 * images, all of which the strict-whitelisted write path rejects, so every entry is rebuilt as a
 * pure reference. Like `presentationContent` — and unlike the shallow-PATCH `presentationSettings` —
 * `presentationPages` is wholesale-replaced when present: the overlay is one cohesive list, so an
 * incoming value replaces the stored sub-pages entirely.
 *
 * Note the Input/Output asymmetry: `presentationContent` accepts an explicit `null` to clear, but
 * `ShortUrlUpdate['presentationPages']` has no `| null` member, so a clear is not expressible on the
 * wire and `undefined` (leave untouched) is the only absence the type admits. The `== null` guard
 * still passes a runtime `null` straight through unchanged rather than crashing on `.subPages`.
 */
export const getShortUrlUpdatePresentationPages = (
  presentationPages: ShortUrlMutationPresentationPages,
): ShortUrlUpdatePayload['presentationPages'] => {
  if (presentationPages == null) {
    return presentationPages;
  }

  return {
    ...(presentationPages.hideNavigation !== undefined ? { hideNavigation: presentationPages.hideNavigation } : {}),
    subPages: presentationPages.subPages.map(getShortUrlUpdateSubPage),
  };
};
